import type { DigitalTwinSettingTarget, DigitalTwinTranslations } from 'api/digitalTwin/digitalTwin.api'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import type { AppliedSettingSeries, AttributeChip } from './appliedSettingValues.types'

function toTargetKey({ level, name, attribute }: DigitalTwinSettingTarget): string {
  return JSON.stringify([level, name, attribute])
}

function toObjectKey({ level, unitName }: AppliedSettingSeries): string {
  return JSON.stringify([level, unitName])
}

/**
 * Narrows the series catalogue to what the user's property filter selects: the selected settings, plus
 * the outcome and optimal series of every object with at least one selected setting. Catalogue order
 * is kept.
 */
export function selectSeries(
  catalogue: AppliedSettingSeries[],
  selectedProperties: DigitalTwinSettingTarget[],
  attributeChips: string[] = []
): AppliedSettingSeries[] {
  const selectedKeys = new Set(selectedProperties.map(toTargetKey))
  const isSelectedSetting = (series: AppliedSettingSeries): boolean =>
    series.kind === 'setting' &&
    selectedKeys.has(toTargetKey(series.property)) &&
    (attributeChips.length === 0 || attributeChips.includes(series.property.attribute))
  const objectsWithSelectedSettings = new Set(catalogue.filter(isSelectedSetting).map(toObjectKey))

  return catalogue.filter((series) =>
    series.kind === 'setting' ? isSelectedSetting(series) : objectsWithSelectedSettings.has(toObjectKey(series))
  )
}

/** One chip per setting attribute present in the series, titled by the attribute alone. */
export function getAttributeChips(
  series: AppliedSettingSeries[],
  translations: DigitalTwinTranslations
): AttributeChip[] {
  const attributes = new Set(series.flatMap((entry) => (entry.kind === 'setting' ? [entry.property.attribute] : [])))
  return [...attributes].map((attribute) => ({ attribute, title: getPrettyName(attribute, translations) }))
}

function isLoadedWithoutValue(dataset: Dataset | undefined): boolean {
  return dataset !== undefined && dataset.status !== 'error' && dataset.values.every((value) => value === null)
}

/**
 * Drops every object with no outcome: one without an outcome series, since no item joins it, and one whose
 * outcome dataset loaded with no value. An outcome dataset that is absent or failed keeps its object,
 * since neither shows that it has no outcome.
 */
export function selectUnitsWithOutcome(series: AppliedSettingSeries[], datasets: Dataset[]): AppliedSettingSeries[] {
  const datasetByReturnId = new Map(datasets.map((dataset) => [dataset.return_id, dataset]))
  const outcomeSeries = series.filter((entry) => entry.kind === 'outcome')
  const objectsWithOutcome = new Set(outcomeSeries.map(toObjectKey))
  const objectsWithoutValue = new Set(
    outcomeSeries.filter((entry) => isLoadedWithoutValue(datasetByReturnId.get(entry.returnId))).map(toObjectKey)
  )

  return series.filter((entry) => {
    const key = toObjectKey(entry)
    return objectsWithOutcome.has(key) && !objectsWithoutValue.has(key)
  })
}
